/**
 * A board's stacks: which columns there are, and what belongs in each.
 *
 * The stacks are the options of the view's single select field, plus one for
 * the rows that have no option -- which is not a special case anybody chose but
 * a fact about the data: a select field is nullable, and those rows have to be
 * somewhere. The server agrees, and keys them under `null` in its answer.
 *
 * Pure, because both halves of a board depend on it: the columns that are drawn,
 * and the decision a realtime event forces -- does this row still belong in this
 * stack. Getting the second wrong moves somebody else's row into the wrong
 * column, which is the failure a board makes most visible.
 */
import type { Field, FieldOf, SelectOption, ViewOf } from '@/api/types'
import type { FilterableGroup } from '../rows/rowMatch'

export interface BoardStack {
  /** The option this stack holds, or null for the rows with none. */
  optionId: number | null
  /** What the column is called: the option's own name, or nothing. */
  option: SelectOption | null
}

/**
 * The stacks, in the order the field offers its options, with the unset one
 * first.
 *
 * First rather than last because it is where a row starts: a row created
 * without a value for the select field appears there, and a column somebody has
 * to scroll sideways to find is a column they will think is missing.
 */
export function boardStacks(field: Field | null): BoardStack[] {
  const options =
    field === null ? [] : ((field as FieldOf<'single_select'>).select_options ?? [])
  return [
    { optionId: null, option: null },
    // `id` is optional on the type because a select option being written in the
    // field editor has no id until it is saved. One on a field the server sent
    // always has one, and a stack that could not name its option could not ask
    // for its rows.
    ...options
      .filter(
        (option): option is SelectOption & { id: number } => option.id !== undefined,
      )
      .map((option) => ({ optionId: option.id, option })),
  ]
}

/** The single select field a board is stacked by, or null if it has none. */
export function stackField(
  view: ViewOf<'board'>,
  fields: readonly Field[],
): Field | null {
  const id = view.single_select_field
  if (id === null || id === undefined) return null
  return fields.find((field) => field.id === id && field.type === 'single_select') ?? null
}

/**
 * The id of the synthetic group the board's own filters are moved into.
 *
 * Negative because the backend numbers real groups from one, so nothing it
 * sends can collide with it -- and a collision would silently reparent
 * somebody's conditions.
 */
const BOARD_FILTER_GROUP = -1

/**
 * The view a single stack's window is asking about.
 *
 * A stack holds the rows that match the view *and* carry this stack's option,
 * so its window's membership question is the view's plus one more condition.
 * That matters beyond the first render: `rowLifecycle` decides per window
 * whether a row still belongs, so without this a row somebody dragged from one
 * column to another would be reported as belonging in both.
 *
 * The board's own conditions are moved into a group rather than left beside the
 * stack's, because the two are combined differently: a view whose `filter_type`
 * is `OR` means "any of these", and appending the stack's condition to that
 * would make a row in *any* stack match *every* stack. Wrapped, the root is an
 * `AND` of "this stack" and "whatever the view says", which is what a board
 * column actually is.
 *
 * `filters_disabled` is not carried across, for the same reason: it says the
 * reader has switched the *view's* filters off, and it cannot be allowed to
 * switch off the thing that makes a column that column.
 */
export function stackView(
  view: ViewOf<'board'>,
  stackFieldId: number,
  optionId: number | null,
): ViewOf<'board'> {
  const disabled = view.filters_disabled === true
  const own = disabled ? [] : (view.filters ?? [])
  const ownGroups = disabled ? [] : (view.filter_groups ?? [])

  return {
    ...view,
    filters_disabled: false,
    filter_type: 'AND',
    filters: [
      {
        // Not a stored filter, so it has no id of its own that means anything.
        id: BOARD_FILTER_GROUP,
        view: view.id,
        field: stackFieldId,
        type: optionId === null ? 'empty' : 'single_select_equal',
        value: optionId === null ? '' : String(optionId),
        preload_values: {},
        group: null,
      },
      // Re-pointed into the wrapper rather than left at the root, where they
      // would be combined with the stack's condition by the root's conjunction.
      ...own.map((filter) => ({
        ...filter,
        group: filter.group ?? BOARD_FILTER_GROUP,
      })),
    ],
    filter_groups: [
      {
        id: BOARD_FILTER_GROUP,
        // The view's own conjunction, which is now this group's rather than the
        // root's -- so "any of these" still means any of *these*.
        filter_type: view.filter_type ?? 'AND',
        parent_group: null,
      } as FilterableGroup,
      // A nested group keeps its parent; a top-level one gains the wrapper.
      ...ownGroups.map((group) => ({
        ...group,
        parent_group: group.parent_group ?? BOARD_FILTER_GROUP,
      })),
    ],
  } as ViewOf<'board'>
}
